export interface Holiday {
  id: string;
  name: string;
  emoji: string[];
  greeting: string;
  priority: number;
  match: (date: Date) => boolean;
}

/**
 * Compute Easter Sunday for a given year using the Anonymous Gregorian algorithm (Computus).
 */
function computeEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function isEasterRange(date: Date): boolean {
  const easter = computeEaster(date.getFullYear());
  const easterDay = easter.getTime();
  const dayMs = 86400000;
  const t = date.getTime();
  // Easter Sunday and Monday
  return t >= easterDay && t < easterDay + 2 * dayMs;
}

function matchMonthDay(month: number, day: number) {
  return (date: Date) => date.getMonth() === month - 1 && date.getDate() === day;
}

function matchMonthDayRange(month: number, startDay: number, endDay: number) {
  return (date: Date) => date.getMonth() === month - 1 && date.getDate() >= startDay && date.getDate() <= endDay;
}

export const HOLIDAYS: Holiday[] = [
  {
    id: "valentines",
    name: "Valentino diena",
    emoji: ["❤️", "💕", "💗", "🩷"],
    greeting: "Su Valentino diena! 💕",
    priority: 5,
    match: matchMonthDay(2, 14),
  },
  {
    id: "lt-independence-restoration",
    name: "Lietuvos valstybės atkūrimo diena",
    emoji: ["🟡", "🟢", "🔴", "🇱🇹"],
    greeting: "Su Vasario 16-ąja! 🇱🇹",
    priority: 10,
    match: matchMonthDay(2, 16),
  },
  {
    id: "lt-independence-day",
    name: "Lietuvos nepriklausomybės atkūrimo diena",
    emoji: ["🟡", "🟢", "🔴", "🇱🇹"],
    greeting: "Su Kovo 11-ąja! 🇱🇹",
    priority: 10,
    match: matchMonthDay(3, 11),
  },
  {
    id: "easter",
    name: "Velykos",
    emoji: ["🥚", "🐣", "🐰", "🌷", "🦋"],
    greeting: "Linksmų Velykų! 🐣",
    priority: 8,
    match: isEasterRange,
  },
  {
    id: "back-to-school",
    name: "Rugsėjo 1-oji",
    emoji: ["📚", "✏️", "🎒", "📐", "🍎"],
    greeting: "Su mokslo metų pradžia! 📚",
    priority: 7,
    match: matchMonthDay(9, 1),
  },
  {
    id: "teachers-day",
    name: "Mokytojų diena",
    emoji: ["🍎", "📖", "🌻", "⭐"],
    greeting: "Su Mokytojų diena! 🌻",
    priority: 7,
    match: matchMonthDay(10, 5),
  },
  {
    id: "halloween",
    name: "Helovinas",
    emoji: ["🎃", "👻", "🦇", "🕸️", "🕷️"],
    greeting: "Linksmo Helovino! 🎃",
    priority: 5,
    match: matchMonthDay(10, 31),
  },
  {
    id: "christmas",
    name: "Kalėdos",
    emoji: ["❄️", "🎄", "⭐", "🎁", "🔔"],
    greeting: "Linksmų Kalėdų! 🎄",
    priority: 9,
    match: matchMonthDayRange(12, 24, 26),
  },
  {
    id: "new-year",
    name: "Naujieji metai",
    emoji: ["🎆", "🎇", "✨", "🥳", "🎉"],
    greeting: "Laimingų Naujųjų Metų! 🎉",
    priority: 9,
    match: (date: Date) => {
      const m = date.getMonth();
      const d = date.getDate();
      return (m === 11 && d === 31) || (m === 0 && d === 1);
    },
  },
  {
    id: "summer-break",
    name: "Vasaros atostogos",
    emoji: ["☀️", "🌊", "🌴", "🦋", "🍦"],
    greeting: "Smagių vasaros atostogų! ☀️",
    priority: 1,
    match: (date: Date) => {
      const m = date.getMonth();
      return m >= 5 && m <= 7; // June (5) through August (7)
    },
  },
];
